import { entries } from "../../../common/util/Object/entries";
import { CountryCode } from "../County/countryCodeAndName";

export interface UserBase {
    userId: number;
    name: string;
    countryCode: CountryCode;
    xp: number;
    level: number;
    avatarPath: string;
    sul: number;
}

export const Plan = {
    Free: 0,
    Chunin: 1,
    Jonin: 2,
    Hokage: 3,
} as const;
export type Plan = (typeof Plan)[keyof typeof Plan];

export function getPlanName(plan: Plan) {
    return entries(Plan).find(([_name, num]) => num === plan)?.[0] ?? "Unknown";
}

export function parsePlanFromString(
    raw: string | null | undefined
): Plan | null {
    if (!raw) return null;

    const trimmed = raw.trim();
    if (!trimmed) return null;

    // 1) 数値として解釈できるなら、Plan の値として妥当かチェック
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
        const values = Object.values(Plan).filter(
            v => typeof v === "number"
        ) as Plan[];
        if (values.includes(numeric as Plan)) {
            return numeric as Plan;
        }
    }

    // 2) 文字列として Plan のキー名と比較（大文字小文字は無視）
    const lower = trimmed.toLowerCase();
    const matched = entries(Plan).find(
        ([name]) => name.toLowerCase() === lower
    );
    return matched ? matched[1] : null;
}

export interface User extends UserBase {
    email: string;
    progress: string | null;
    coin: number;
    roulettePlay: number;
    friendExpansion: number;
    bio: string;
    bioLinks: BioLink[];
    loginStreak: number;
    xd: number;
    chakra: number;
    plan: Plan;
    hideNinjaAnimation: boolean;
    canStartChuninTrial?: boolean;
}

export interface UserInfoForAdmin extends UserBase {
    email: string;
    progress: string | null;
    coin: number;
    roulettePlay: number;
    friendExpansion: number;
    bio: string;
    bioLinks: BioLink[];
    memo: string;
    memorizedFlashcards: number;
    xd: number;
    dr: number;
    chakra: number;
    plan: Plan;
    unlockedVocabQuizCount: number;
    unlockedGrammarTestCount: number;
    unlockedKanjiTestCount: number;
    privateVocabListWordCount: number;
    initialChakra: number | null;
    openedAudioDownloadCount: number;
    isInChuninTrial: boolean;
}

export interface OtherUser extends UserBase {
    bio: string;
    bioLinks: BioLink[];
    loginStreak: number;
}

export interface BioLink {
    url: string;
    title: string | null;
}

interface FriendRequestFromMe {
    approved: boolean;
    canceled: boolean;
}
interface FriendRequestFromThem {
    approved: boolean;
    requestTime: string;
}
export interface RelatedUser {
    userId: UserBase["userId"];
    requestFromMe: FriendRequestFromMe | null;
    requestFromThem: FriendRequestFromThem | null;
    isFriend: boolean;
    isNewFriend: boolean;
}

export interface UserForRanking extends UserBase {}

export interface UserForFriendList extends UserBase {
    isNewFriend: boolean;
}

export interface UserForMessageList extends UserBase {
    unreadMessageCount: number;
    lastMessageMinutesAgo: number;
}

export interface ActiveUser extends UserBase {
    bio: string;
    online: boolean;
}

export interface FlashcardCreator extends UserBase {
    bookmarkCount: number;
}
